import { useMutation, type UseMutationOptions } from '@tanstack/react-query'

import type { ISFPUploadResponse } from '../types'

import { isfpUploadApi } from './isfp-upload.api'

interface UploadISFPFileParams {
  file: File
  buildingUuid: string
}

interface UploadISFPFileWithProgressParams extends UploadISFPFileParams {
  onProgress?: (progress: number) => void
  onAbortReady?: (abort: () => void) => void
}

/**
 * Hook for uploading ISFP files without progress tracking
 */
export const useUploadISFPFile = (
  options?: UseMutationOptions<ISFPUploadResponse, Error, UploadISFPFileParams>
) => {
  return useMutation({
    mutationFn: ({ file, buildingUuid }: UploadISFPFileParams) =>
      isfpUploadApi.uploadISFPFile(file, buildingUuid),
    ...options,
  })
}

/**
 * Hook for uploading ISFP files with progress tracking
 */
export const useUploadISFPFileWithProgress = (
  options?: UseMutationOptions<ISFPUploadResponse, Error, UploadISFPFileWithProgressParams>
) => {
  return useMutation({
    mutationFn: ({
      file,
      buildingUuid,
      onProgress,
      onAbortReady,
    }: UploadISFPFileWithProgressParams) => {
      const { promise, abort } = isfpUploadApi.uploadISFPFileWithProgress(
        file,
        buildingUuid,
        onProgress
      )

      // Provide the abort function to the caller
      if (onAbortReady) {
        onAbortReady(abort)
      }

      return promise
    },
    ...options,
  })
}
